import React, { useEffect, useRef, useState, useCallback } from 'react';
import terminalData from '../../data/terminal.json';

// Decision tree nodes — loaded from JSON
const TREE = terminalData.tree;

const CHAR_DELAY = 28;

export default function TerminalSection() {
  const sectionRef = useRef(null);
  const inputRef   = useRef(null);
  const bodyRef    = useRef(null);

  const [started, setStarted]       = useState(false);
  const [nodeId, setNodeId]         = useState('start');
  const [history, setHistory]       = useState([]);
  const [typingText, setTypingText] = useState('');
  const [isTyping, setIsTyping]     = useState(false);
  const [inputActive, setInputActive] = useState(false);
  const [userInput, setUserInput]   = useState('');
  const [inputError, setInputError] = useState('');
  const timers = useRef([]);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  const scrollBottom = () => {
    setTimeout(() => {
      if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }, 50);
  };

  const typeText = useCallback((text, onDone) => {
    clearTimers();
    setIsTyping(true);
    setTypingText('');
    setInputActive(false);
    for (let i = 0; i <= text.length; i++) {
      const t = setTimeout(() => {
        setTypingText(text.slice(0, i));
        if (i === text.length) {
          setIsTyping(false);
          setInputActive(true);
          setTimeout(() => inputRef.current && inputRef.current.focus(), 60);
          if (onDone) onDone();
        }
      }, i * CHAR_DELAY);
      timers.current.push(t);
    }
  }, []);

  const goToNode = useCallback((id) => {
    const node = TREE[id];
    if (!node) return;
    setNodeId(id);
    setInputError('');
    if (node.answer) {
      // show answer then offer back option
      typeText(node.text);
    } else {
      typeText(node.question);
    }
    scrollBottom();
  }, [typeText]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
          observer.disconnect();
          setTimeout(() => goToNode('start'), 500);
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => { observer.disconnect(); clearTimers(); };
  }, [started, goToNode]);

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter' || !inputActive || isTyping) return;
    const val = userInput.trim();
    const node = TREE[nodeId];

    if (node.answer) {
      // on answer node, any key goes back
      const entry = {
        type: 'answer-nav',
        userInput: val || 'back',
        nextId: node.back,
      };
      setHistory(prev => [...prev, { nodeId, node, entry }]);
      setUserInput('');
      setTypingText('');
      setInputActive(false);
      setTimeout(() => goToNode(node.back), 200);
      scrollBottom();
      return;
    }

    // find matching option
    const option = node.options.find(o => o.key === val);
    if (!option) {
      setInputError(`Invalid option "${val}". Enter a number from the list.`);
      setUserInput('');
      return;
    }

    setInputError('');
    const entry = { type: 'choice', userInput: val, label: option.label, nextId: option.next };
    setHistory(prev => [...prev, { nodeId, node, entry }]);
    setUserInput('');
    setTypingText('');
    setInputActive(false);
    setTimeout(() => goToNode(option.next), 200);
    scrollBottom();
  };

  const currentNode = TREE[nodeId];

  return (
    <section id="terminal" className="dt-section" ref={sectionRef} style={{}}>
      <style>{`
        .dt-section {
          background: var(--eventflow-black);
          padding: 100px 0 120px;
          position: relative; overflow: hidden;
        }
        .dt-section::before {
          content: '';
          position: absolute; inset: 0;
          background: radial-gradient(ellipse at 50% 60%, rgba(0,255,65,0.04) 0%, transparent 60%);
          pointer-events: none;
        }
        /* header */
        .dt-header { text-align: center; margin-bottom: 56px; position: relative; z-index: 1; }
        .dt-header__tag {
          display: inline-flex; align-items: center; gap: 8px;
          font-family: var(--eventflow-font); font-size: 12px; font-weight: 700;
          letter-spacing: 3px; text-transform: uppercase;
          color: var(--eventflow-base); margin-bottom: 16px;
        }
        .dt-header__tag::before, .dt-header__tag::after {
          content: ''; display: inline-block; width: 28px; height: 1px;
          background: var(--eventflow-base); opacity: 0.5;
        }
        .dt-header__title {
          font-family: var(--eventflow-font); font-size: 48px; font-weight: 900;
          color: #fff; line-height: 1.1; letter-spacing: -1px; margin-bottom: 14px;
        }
        .dt-header__title span { color: var(--eventflow-base); }
        .dt-header__sub {
          font-family: var(--eventflow-font); font-size: 16px;
          color: rgba(255,255,255,0.45); max-width: 520px; margin: 0 auto; line-height: 1.7;
        }
        /* monitor */
        .dt-monitor-wrap { position: relative; z-index: 1; display: flex; flex-direction: column; align-items: center; }
        .dt-monitor {
          width: 100%; max-width: 860px;
          background: linear-gradient(160deg, #1c1c20 0%, #111113 60%, #0a0a0c 100%);
          border-radius: 16px 16px 8px 8px; padding: 14px 14px 10px;
          box-shadow: 0 0 0 1px rgba(0,255,65,0.12), 0 40px 100px rgba(0,0,0,0.85),
                      0 0 80px rgba(0,255,65,0.07), inset 0 1px 0 rgba(255,255,255,0.06);
          position: relative;
        }
        .dt-monitor::before {
          content: ''; position: absolute; top: 7px; left: 50%; transform: translateX(-50%);
          width: 6px; height: 6px; border-radius: 50%; background: #1a1a1a;
          box-shadow: 0 0 0 1px rgba(0,255,65,0.15);
        }
        .dt-screen {
          background: #000; border-radius: 6px; overflow: hidden;
          border: 1px solid rgba(0,255,65,0.12);
          box-shadow: inset 0 0 60px rgba(0,0,0,0.9), 0 0 20px rgba(0,255,65,0.06);
          position: relative;
        }
        .dt-screen::before {
          content: ''; position: absolute; top: 0; left: 0; right: 0; height: 35%;
          background: linear-gradient(180deg, rgba(0,255,65,0.018) 0%, transparent 100%);
          pointer-events: none; z-index: 10; border-radius: 6px 6px 0 0;
        }
        .dt-bar {
          background: #0a0a0a; padding: 10px 16px;
          display: flex; align-items: center; justify-content: space-between;
          border-bottom: 1px solid rgba(0,255,65,0.08);
        }
        .dt-bar__dots { display: flex; gap: 7px; }
        .dt-dot { width: 12px; height: 12px; border-radius: 50%; }
        .dt-dot--r { background: #ff5f57; box-shadow: 0 0 5px rgba(255,95,87,0.6); }
        .dt-dot--y { background: #febc2e; box-shadow: 0 0 5px rgba(254,188,46,0.6); }
        .dt-dot--g { background: #28c840; box-shadow: 0 0 5px rgba(40,200,64,0.6); }
        .dt-bar__title {
          font-family: 'Courier New', monospace; font-size: 12px;
          color: rgba(0,255,65,0.35); text-shadow: 0 0 6px rgba(0,255,65,0.2);
        }
        .dt-bar__hint { font-family: 'Courier New', monospace; font-size: 11px; color: rgba(0,255,65,0.2); }
        /* body */
        .dt-body {
          padding: 20px 24px 24px; font-family: 'Courier New', monospace;
          font-size: 14px; line-height: 1.85;
          min-height: 440px; max-height: 500px; overflow-y: auto;
          background: #000; position: relative;
          box-shadow: inset 0 0 80px rgba(0,20,0,0.5);
          scroll-behavior: smooth;
        }
        .dt-body::-webkit-scrollbar { width: 4px; }
        .dt-body::-webkit-scrollbar-track { background: transparent; }
        .dt-body::-webkit-scrollbar-thumb { background: rgba(0,255,65,0.15); border-radius: 2px; }
        .dt-body::after {
          content: ''; position: absolute; inset: 0;
          background: repeating-linear-gradient(0deg, transparent 0px, transparent 3px, rgba(0,0,0,0.07) 3px, rgba(0,0,0,0.07) 4px);
          pointer-events: none; z-index: 20;
        }
        /* history block */
        .dt-block { margin-bottom: 16px; }
        .dt-divider { border: none; border-top: 1px solid rgba(0,255,65,0.07); margin: 12px 0; }
        /* question */
        .dt-q { color: rgba(0,255,65,0.6); font-style: italic; margin-bottom: 6px; white-space: pre-line; }
        /* options */
        .dt-opts { margin-bottom: 6px; }
        .dt-opt { color: rgba(0,255,65,0.5); padding-left: 4px; }
        .dt-opt-key { color: #00ff41; font-weight: 700; text-shadow: 0 0 8px #00ff41; }
        /* user choice echo */
        .dt-choice {
          display: flex; align-items: center; gap: 8px; margin-bottom: 4px;
          color: #00ff41; text-shadow: 0 0 6px rgba(0,255,65,0.5);
        }
        .dt-choice-arrow { color: #00e676; font-weight: 700; }
        /* answer */
        .dt-answer {
          padding-left: 4px; margin-bottom: 4px;
          animation: dtIn 0.2s ease forwards; opacity: 0;
          white-space: pre-line;
        }
        @keyframes dtIn { from{opacity:0;transform:translateX(-4px)} to{opacity:1;transform:translateX(0)} }
        .dt-ans-str    { color: #b9f6ca; text-shadow: 0 0 4px rgba(185,246,202,0.2); }
        .dt-ans-bright { color: #00ff41; font-weight: 700; text-shadow: 0 0 12px #00ff41; }
        .dt-ans-arr    { color: #69ff47; text-shadow: 0 0 6px rgba(105,255,71,0.4); }
        .dt-back-hint  { color: rgba(0,255,65,0.3); font-size: 12px; font-style: italic; margin-top: 4px; }
        /* current typing */
        .dt-typing { color: rgba(0,255,65,0.6); font-style: italic; white-space: pre-line; }
        .dt-cursor {
          display: inline-block; width: 7px; height: 14px;
          background: rgba(0,255,65,0.5);
          animation: dtBlink 0.8s step-end infinite; vertical-align: middle; margin-left: 2px;
        }
        @keyframes dtBlink { 0%,100%{opacity:1} 50%{opacity:0} }
        /* current options */
        .dt-cur-opts { margin: 6px 0; }
        /* input */
        .dt-input-line { display: flex; align-items: center; gap: 8px; margin-top: 6px; }
        .dt-ps { color: #00ff41; font-weight: 700; text-shadow: 0 0 10px #00ff41; flex-shrink: 0; }
        .dt-input {
          background: transparent; border: none; outline: none;
          font-family: 'Courier New', monospace; font-size: 14px;
          color: #00ff41; text-shadow: 0 0 8px rgba(0,255,65,0.6);
          caret-color: #00ff41; flex: 1; min-width: 0;
        }
        .dt-input::placeholder { color: rgba(0,255,65,0.2); font-style: italic; }
        .dt-error { color: #ff4444; font-size: 12px; margin-top: 4px; padding-left: 4px; }
        /* neck + base */
        .dt-neck {
          width: 80px; height: 26px;
          background: linear-gradient(180deg, #1a1a1e 0%, #131315 100%);
          clip-path: polygon(15% 0%, 85% 0%, 100% 100%, 0% 100%);
          box-shadow: 0 4px 12px rgba(0,0,0,0.5);
        }
        .dt-base {
          width: 220px; height: 13px;
          background: linear-gradient(180deg, #1e1e22 0%, #141416 100%);
          border-radius: 0 0 8px 8px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,255,65,0.06);
        }
        /* bottom */
        .dt-bottom { display: flex; flex-direction: column; align-items: center; gap: 20px; margin-top: 44px; position: relative; z-index: 1; }
        .dt-tags { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; }
        .dt-tag {
          font-family: 'Courier New', monospace; font-size: 11px; font-weight: 700;
          color: rgba(0,255,65,0.65); background: rgba(0,255,65,0.06);
          border: 1px solid rgba(0,255,65,0.15); border-radius: 4px; padding: 4px 12px;
          text-shadow: 0 0 6px rgba(0,255,65,0.3); transition: all 0.25s;
        }
        .dt-tag:hover { background: rgba(0,255,65,0.12); color: #00ff41; }
        .dt-cta { display: flex; gap: 14px; flex-wrap: wrap; justify-content: center; }
        @media (max-width: 991px) {
          .dt-header__title { font-size: 36px; }
          .dt-body { font-size: 12.5px; padding: 16px 16px 20px; min-height: 380px; max-height: 440px; }
        }
        @media (max-width: 576px) {
          .dt-section { padding: 70px 0 90px; }
          .dt-header__title { font-size: 28px; }
          .dt-monitor { padding: 10px 10px 8px; border-radius: 12px 12px 6px 6px; }
          .dt-body { font-size: 11.5px; padding: 12px 12px 16px; min-height: 320px; max-height: 380px; }
          .dt-neck { width: 60px; height: 20px; }
          .dt-base { width: 160px; height: 10px; }
          .dt-bar__title, .dt-bar__hint { display: none; }
          .dt-input { font-size: 12px; }
        }
      `}</style>

      <div className="container">
        <div className="dt-header wow fadeInUp" data-wow-delay="100ms">
          <div className="dt-header__tag">{terminalData.section.eyebrow}</div>
          <h2 className="dt-header__title">{terminalData.section.title} <span>{terminalData.section.titleAccent}</span></h2>
          <p className="dt-header__sub">
            {terminalData.section.subtitle}
          </p>
        </div>

        <div className="dt-monitor-wrap wow fadeInUp" data-wow-delay="200ms">
          <div className="dt-monitor">
            <div className="dt-screen">
              <div className="dt-bar">
                <div className="dt-bar__dots">
                  <span className="dt-dot dt-dot--r" />
                  <span className="dt-dot dt-dot--y" />
                  <span className="dt-dot dt-dot--g" />
                </div>
                <span className="dt-bar__title">bsides@coimbatore:~$ — explore</span>
                <span className="dt-bar__hint">Type a number + Enter</span>
              </div>

              <div className="dt-body" ref={bodyRef} onClick={() => inputRef.current && inputRef.current.focus()}>

                {/* History */}
                {history.map((item, i) => (
                  <div className="dt-block" key={i}>
                    {i > 0 && <hr className="dt-divider" />}
                    <div className="dt-q">{item.node.answer ? item.node.text : item.node.question}</div>
                    {!item.node.answer && item.node.options && (
                      <div className="dt-opts">
                        {item.node.options.map(o => (
                          <div className="dt-opt" key={o.key}>
                            <span className="dt-opt-key">[{o.key}]</span> {o.label}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="dt-choice">
                      <span className="dt-choice-arrow">›</span>
                      <span>{item.entry.userInput}{item.entry.label ? ` — ${item.entry.label}` : ''}</span>
                    </div>
                  </div>
                ))}

                {/* Current node */}
                {typingText !== '' && (
                  <div className="dt-block">
                    {history.length > 0 && <hr className="dt-divider" />}

                    {/* Typing text */}
                    <div className="dt-typing">
                      {typingText}
                      {isTyping && <span className="dt-cursor" />}
                    </div>

                    {/* Options (only for non-answer nodes, after typing done) */}
                    {!isTyping && currentNode && !currentNode.answer && (
                      <div className="dt-cur-opts">
                        {currentNode.options.map(o => (
                          <div className="dt-opt" key={o.key}>
                            <span className="dt-opt-key">[{o.key}]</span> {o.label}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Answer styling */}
                    {!isTyping && currentNode && currentNode.answer && (
                      <div className="dt-back-hint">Press Enter to go back</div>
                    )}

                    {/* Input */}
                    {inputActive && (
                      <>
                        <div className="dt-input-line">
                          <span className="dt-ps">$</span>
                          <input
                            ref={inputRef}
                            className="dt-input"
                            type="text"
                            value={userInput}
                            onChange={e => { setUserInput(e.target.value); setInputError(''); }}
                            onKeyDown={handleKeyDown}
                            placeholder={currentNode && currentNode.answer ? 'press Enter...' : 'enter option number...'}
                            autoComplete="off"
                            spellCheck="false"
                            maxLength={2}
                          />
                        </div>
                        {inputError && <div className="dt-error">✗ {inputError}</div>}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="dt-neck" />
          <div className="dt-base" />
        </div>

        <div className="dt-bottom">
          <div className="dt-tags">
            {terminalData.section.tags.map((t,i) => (
              <span className="dt-tag" key={i}>{t}</span>
            ))}
          </div>
          <div className="dt-cta">
            <a href={terminalData.section.ctaButtonLink} className="thm-btn">{terminalData.section.ctaButtonText} <span className="icon-arrow-right" /></a>
            <a href={terminalData.section.ctaButtonLink2} className="thm-btn-outline">{terminalData.section.ctaButtonText2} <span className="icon-arrow-right" /></a>
          </div>
        </div>
      </div>
    </section>
  );
}


